import { TemplateElement, html, defineElement, optionalAttribute } from '@webtides/element-js';
import paginate from '../../utils/paginate.js';
import i18n from '../../utils/i18n.js';
import style from './pagination.css';
import '../icon/icon.js';

/**
 * @property {number} totalItems
 * @property {number} currentPage
 * @property {number} pageSize
 * @property {number} maxPages
 * @property {string} currentPageParameterName
 */
export default class Pagination extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            totalItems: 0,
            currentPage: 1,
            pageSize: 10,
            maxPages: 5,
            currentPageParameterName: 'page',
        };
    }

    get pagination() {
        return paginate(this.totalItems, this.currentPage, this.pageSize, this.maxPages);
    }

    get previousPage() {
        const previousPage = this.currentPage - 1;
        return previousPage < 1 ? 1 : previousPage;
    }

    get nextPage() {
        const nextPage = this.currentPage + 1;
        return nextPage > this.pagination?.totalPages ? this.pagination.totalPages : nextPage;
    }

    template() {
        const pageLinkActiveClasses =
            'z-10 bg-white font-bold border !border-0 !border-y-2 !border-b-green-medium rounded-0 !border-t-transparent';
        const pageLinkDefaultClasses = 'hover:bg-green-light25 active:opacity-50 font-medium';
        const pageLinkArrowDeactivated = 'text-gray-40';

        return html`
            <div class="flex gap-16 flex-col sm:flex-row items-center justify-center">
                <div>
                    <nav class="relative z-0 inline-flex gap-2" aria-label="${i18n('pagination.label')}">
                        <a
                            href="${`?${this.currentPageParameterName}=${this.previousPage}`}"
                            rel="prev"
                            class="relative inline-flex items-center place-content-center text-sm font-bold rounded-lg hover:bg-green-light25 active:opacity-50 w-32"
                        >
                            <span class="sr-only">${i18n('pagination.previous')}</span>
                            <mb-icon
                                name="app--cta-arrow-left"
                                class="${this.currentPage <= 1 ? pageLinkArrowDeactivated : ''} h-16 w-16"
                            ></mb-icon>
                        </a>
                        ${(this.pagination?.pages || []).map(
                            (page) => html`
                                <a
                                    href="${`?${this.currentPageParameterName}=${page}`}"
                                    ${optionalAttribute(page === this.pagination?.currentPage, 'aria-current', 'page')}
                                    class="${page === this.pagination?.currentPage
                                        ? pageLinkActiveClasses
                                        : pageLinkDefaultClasses} relative inline-flex items-center place-content-center text-base rounded-lg aspect-square w-32 box-content"
                                >
                                    ${page}
                                </a>
                            `,
                        )}
                        <a
                            href="${`?${this.currentPageParameterName}=${this.nextPage}`}"
                            rel="next"
                            class="relative inline-flex items-center place-content-center text-sm font-bold rounded-lg hover:bg-green-light25 active:opacity-50 w-32"
                        >
                            <span class="sr-only">${i18n('pagination.next')}</span>
                            <mb-icon
                                name="app--cta-arrow-right"
                                class="${this.currentPage >= this.maxPages ? pageLinkArrowDeactivated : ''} h-16 w-16"
                            ></mb-icon>
                        </a>
                    </nav>
                </div>
            </div>
        `;
    }
}

defineElement('mb-pagination', Pagination);
